import { ApiClient, ApiError, FRIENDLY, NetworkError } from '@tradeoutlet/shared';
import { configureData } from '@tradeoutlet/data';
import { env } from './env';
import { connection } from './connection';

let tokenGetter: () => Promise<string | null> = async () => null;
export function setTokenGetter(fn: () => Promise<string | null>) { tokenGetter = fn; }

let unauthorized: () => void = () => undefined;
/** t269u: what to do when the API says the session is no longer valid (the auth provider takes the person to sign in). */
export function setUnauthorizedHandler(fn: () => void) { unauthorized = fn; }
let mfaRequired: () => void = () => undefined;
/** t413u: the API refused this session until the two-factor code is entered (403 mfa_required). */
export function setMfaRequiredHandler(fn: () => void) { mfaRequired = fn; }
export function onMfaRequired() { mfaRequired(); }
/** t363u: another device ended some of this person's sessions; re-check ours (refresh, or sign in if it was ended). */
export function recheckSession() { unauthorized(); }

/** t269u: requests being retried right now (a deploy restart, a dropped connection); drives the "Reconnecting…" note. */
let retryingCount = 0;
const retryListeners = new Set<() => void>();
export const retryState = {
  subscribe(fn: () => void) { retryListeners.add(fn); return () => { retryListeners.delete(fn); }; },
  get: () => retryingCount > 0,
};

/**
 * Shared typed API client. Identity is always derived server-side from the bearer token. t269u: 502/503/504 and
 * network failures are retried here for about 15–20 s (packages/shared/src/apiRetry.ts), so pages need no handlers.
 */
export const api = new ApiClient({
  baseUrl: env.apiUrl,
  getAccessToken: () => tokenGetter(),
  isOffline: () => !connection.isDeviceOnline(),
  onRetrying: on => { retryingCount = Math.max(0, retryingCount + (on ? 1 : -1)); retryListeners.forEach(l => l()); },
  onUnauthorized: () => unauthorized(),
  // t574u: the server answered, so we're connected (clears a stale offline / reconnecting indicator).
  onResponse: status => { if (status < 500) connection.contact(); },
});
// The shared data layer (packages/data) fetches through this client.
configureData({ api });

/**
 * t560u: the form sent a field the API doesn't take. Usually a tab left open across an update (a reload fixes it), but
 * it can also be our bug, so don't claim the page is out of date or suggest they did something wrong.
 */
export const FORM_MISMATCH = 'Please reload the page and try again. If it still won’t save, the problem is on our side, not with what you entered.';

/** Raw class-validator text from the API's DTO whitelist ("property x should not exist") is a client bug, not something a person can fix. */
const RAW_VALIDATION = /\bproperty \w+ should not exist\b|\bmust be an? (?:string|number|boolean|integer|array|object|UUID)\b|\bshould not be empty\b/;

/** "budget_pence" -> "Budget", "assignee_ids" -> "Assignees", "due_date" -> "Due date". */
function fieldLabel(prop: string, labels?: Record<string, string>): string {
  const key = prop.split('.').pop() ?? prop;
  if (labels?.[key]) return labels[key];
  const base = key.replace(/_(ids)$/, 's').replace(/_(id|pence|minutes|at)$/, '').replace(/_/g, ' ').trim();
  return base ? base[0].toUpperCase() + base.slice(1) : 'A field';
}

/**
 * t168u: one class-validator message ("budget_pence must not be greater than 100") as what to fix ("Budget is too
 * large"). Returns null for "property x should not exist": the form sent something the API doesn't take, which the
 * person can't fix (see FORM_MISMATCH).
 */
export function describeValidation(raw: string, labels?: Record<string, string>): string | null {
  const msg = raw.trim();
  if (/^property \S+ should not exist$/.test(msg)) return null;
  const m = /^(?:each value in )?(?:nested property )?([\w.[\]]+) (.*)$/.exec(msg);
  if (!m) return msg;
  const label = fieldLabel(m[1].replace(/\[\d+\]/g, ''), labels);
  const rule = m[2];
  let n: RegExpExecArray | null;
  if ((n = /must not be greater than (\d+)/.exec(rule))) return `${label} is too large`;
  if ((n = /must not be less than (-?\d+)/.exec(rule))) return n[1] === '0' ? `${label} can't be negative` : `${label} is too small`;
  if (/must be an integer/.test(rule)) return `${label} must be a whole number`;
  if (/must be a number/.test(rule)) return `${label} must be a number`;
  if ((n = /must be longer than or equal to (\d+) characters/.exec(rule))) return n[1] === '1' ? `${label} is required` : `${label} must be at least ${n[1]} characters`;
  if ((n = /must be shorter than or equal to (\d+) characters/.exec(rule))) return `${label} is too long (up to ${n[1]} characters)`;
  if (/should not be empty|must be a string|must be defined/.test(rule)) return `${label} is required`;
  if ((n = /must contain no more than (\d+) elements/.exec(rule))) return `${label}: choose up to ${n[1]}`;
  if (/must be a UUID/.test(rule)) return `${label} isn't recognised. Choose it again from the list`;
  if (/must be one of the following values/.test(rule)) return `${label}: choose one of the options`;
  if (/must match .* regular expression|ISO 8601/.test(rule)) return /date|time/i.test(label) ? `${label} isn't a valid ${/time/i.test(label) && !/date/i.test(label) ? 'time' : 'date'}` : `${label} isn't in the right format`;
  return `${label} isn't valid`;
}

/**
 * User-facing text for an error. Pass `fallback` (e.g. "Couldn't save settings") to lead the message, and `labels`
 * to name fields as the form shows them. Validation errors never show raw text: they say which field to fix, or,
 * when the form itself sent something wrong, to reload. The details go to the console.
 */
export function errorMessage(e: unknown, fallback?: string, labels?: Record<string, string>): string {
  if (e instanceof NetworkError) { console.warn('[api] no answer', e.attempts, 'tries', e.cause); return e.message; }
  if (e instanceof ApiError) {
    if (e.status === 0) return FRIENDLY.updating;
    const list = (e.body as { message?: unknown } | null)?.message;
    const isValidation = e.status === 400 && (Array.isArray(list) || RAW_VALIDATION.test(e.message));
    if (isValidation) {
      console.error('[api] validation error', e.message, e);
      const raws = Array.isArray(list) ? list.map(String) : e.message.split('; ');
      const fixes = [...new Set(raws.map(r => describeValidation(r, labels)))];
      const lead = fallback ?? "That didn't go through";
      if (fixes.includes(null) || fixes.length === 0) return `${lead}. ${FORM_MISMATCH}`;
      return `${lead}. ${fixes.map(f => `${f}.`).join(' ')}`;
    }
    // t269u: e.message is already plain en-GB (shared friendlyApiMessage); the code and raw text go to the console.
    if (e.status >= 500 || e.status === 0) console.warn(`[api] ${e.status}`, e.detail);
    return e.message || fallback || FRIENDLY.generic;
  }
  if (e instanceof TypeError && /fetch|network|load failed/i.test(e.message)) return navigator.onLine === false ? FRIENDLY.offline : FRIENDLY.updating;
  return e instanceof Error ? e.message : String(e);
}
export { ApiError, NetworkError };
